import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { SessionManager } from './sessionManager'

// =====================================================================
// EGL v12 Frontend - UNIFIED SMART ROUTER
// Synergizes with backend smart-router.ts for complete route management
// =====================================================================

interface User {
  id: string
  username: string
  email: string
  role: 'USER' | 'ADMIN'
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ENABLED' | 'DISABLED'
  is_active: boolean
  rights: 'ADMIN' | 'CREATOR' | 'READ_ONLY'
  location?: string
}

interface RouteContext {
  currentPath: string
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  navigate: (path: string) => void
  login: (credentials: { username: string; password: string }) => Promise<boolean>
  logout: () => void
  checkAuth: () => Promise<void>
}

const SmartRouterContext = createContext<RouteContext | null>(null)

export function useRouter() {
  const context = useContext(SmartRouterContext)
  if (!context) {
    throw new Error('useRouter must be used within SmartRouterProvider')
  }
  return context
}

interface SmartRouterProviderProps {
  children: React.ReactNode
}

export const SmartRouterProvider: React.FC<SmartRouterProviderProps> = ({ children }) => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname)
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [lastActivity, setLastActivity] = useState(Date.now())

  // Navigate function with history management
  const navigate = useCallback((path: string) => {
    window.history.pushState({}, '', path)
    setCurrentPath(path)
    
    // Send analytics to backend smart router (disabled for now)
    // TODO: Implement analytics endpoint
    console.log('Navigation:', { path, business_unit: getBusinessUnit(path) })
  }, [user])

  // Determine business unit based on path (syncs with backend)
  const getBusinessUnit = (path: string): string => {
    if (path.startsWith('/login') || path.startsWith('/register')) return 'authentication'
    if (path.startsWith('/admin')) return 'administration'
    if (path.startsWith('/certificates')) return 'certificates'
    if (path.startsWith('/dashboard')) return 'user-interface'
    return 'core'
  }

  // Login function
  const login = useCallback(async (credentials: { username: string; password: string }): Promise<boolean> => {
    try {
      setIsLoading(true)
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      })

      const data = await response.json()

      if (data.success && data.data?.user) {
        // Store JWT token
        localStorage.setItem('egl_token', data.data.token)
        
        // Reset session monitoring for fresh session
        SessionManager.resetSessionMonitoring()
        
        // Set user data
        setUser(data.data.user)
        setIsAuthenticated(true)
        
        // Navigate based on role and status
        if (data.data.user.role === 'ADMIN') {
          navigate('/dashboard')
        } else if (data.data.user.status === 'APPROVED' && data.data.user.is_active) {
          navigate('/dashboard')
        } else {
          navigate('/pending-approval')
        }
        
        return true
      }
      
      return false
    } catch (error) {
      console.error('Login error:', error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('egl_token')
    SessionManager.stopSessionMonitoring()
    setUser(null)
    setIsAuthenticated(false)
    navigate('/login')
  }, [navigate])

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('egl_token')
      if (!token) {
        setIsLoading(false)
        return
      }

      const response = await fetch('/api/auth/verify', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      const data = await response.json()

      if (data.success && data.data?.user) {
        setUser(data.data.user)
        setIsAuthenticated(true)
      } else {
        localStorage.removeItem('egl_token')
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      localStorage.removeItem('egl_token')
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Session timeout management (15 minutes)
  useEffect(() => {
    const SESSION_TIMEOUT = 15 * 60 * 1000 // 15 minutes in milliseconds
    
    const checkSessionTimeout = () => {
      if (isAuthenticated && Date.now() - lastActivity > SESSION_TIMEOUT) {
        console.log('Session timed out due to inactivity')
        logout()
      }
    }
    
    // Check every minute
    const timeoutInterval = setInterval(checkSessionTimeout, 60 * 1000)
    
    // Activity listeners
    const updateActivity = () => setLastActivity(Date.now())
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true)
    })
    
    return () => {
      clearInterval(timeoutInterval)
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true)
      })
    }
  }, [isAuthenticated, lastActivity, logout])

  // Check authentication on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const contextValue: RouteContext = {
    currentPath,
    user,
    isAuthenticated,
    isLoading,
    navigate,
    login,
    logout,
    checkAuth
  }

  return (
    <SmartRouterContext.Provider value={contextValue}>
      {children}
    </SmartRouterContext.Provider>
  )
}

// Route protection component
interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireRole?: 'USER' | 'ADMIN'
  requireStatus?: 'APPROVED' | 'ENABLED'
  requireRights?: 'ADMIN' | 'CREATOR' | 'READ_ONLY'
  fallback?: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireRole,
  requireStatus,
  requireRights,
  fallback
}) => {
  const { user, isAuthenticated, isLoading, navigate } = useRouter()

  useEffect(() => {
    if (isLoading) return

    if (requireAuth && !isAuthenticated) {
      navigate('/login')
      return
    }

    if (requireRole && user?.role !== requireRole) {
      navigate('/unauthorized')
      return
    }

    if (requireStatus && user?.status !== requireStatus) {
      navigate('/pending-approval')
      return
    }

    if (requireRights && user?.rights !== requireRights) {
      navigate('/unauthorized')
      return
    }
  }, [isAuthenticated, user, isLoading, requireAuth, requireRole, requireStatus, requireRights, navigate])

  if (isLoading) {
    return <div className="loading-spinner">Loading...</div>
  }

  if (requireAuth && !isAuthenticated) {
    return fallback || null
  }

  if (requireRole && user?.role !== requireRole) {
    return fallback || <div>Unauthorized</div>
  }

  if (requireStatus && user?.status !== requireStatus) {
    return fallback || <div>Pending Approval</div>
  }

  if (requireRights && user?.rights !== requireRights) {
    return fallback || <div>Insufficient Rights</div>
  }

  return <>{children}</>
}

// Smart Route component for declarative routing
interface SmartRouteProps {
  path: string
  component: React.ComponentType<any>
  exact?: boolean
  requireAuth?: boolean
  requireRole?: 'USER' | 'ADMIN'
  requireStatus?: 'APPROVED' | 'ENABLED'
}

export const SmartRoute: React.FC<SmartRouteProps> = ({
  path,
  component: Component,
  exact = false,
  requireAuth,
  requireRole,
  requireStatus
}) => {
  const { currentPath } = useRouter()

  const isMatch = exact 
    ? currentPath === path
    : currentPath.startsWith(path)

  if (!isMatch) return null

  return (
    <ProtectedRoute
      requireAuth={requireAuth}
      requireRole={requireRole}
      requireStatus={requireStatus}
    >
      <Component />
    </ProtectedRoute>
  )
} 